'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

const VISITOR_ID_KEY = 'dntech_visitor_id';
const LAST_SENT_KEY = 'dntech_last_visit_sent';

function getVisitorId() {
  if (typeof window === 'undefined') return '';

  const existing = localStorage.getItem(VISITOR_ID_KEY);
  if (existing && existing.length >= 6) return existing;

  const next =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  localStorage.setItem(VISITOR_ID_KEY, next);
  return next;
}

function shouldSend(pathname: string) {
  if (typeof window === 'undefined') return false;
  const now = Date.now();

  try {
    const raw = localStorage.getItem(LAST_SENT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { path?: string; ts?: number };
      if (
        parsed?.path === pathname &&
        typeof parsed.ts === 'number' &&
        now - parsed.ts < 10 * 60 * 1000
      ) {
        return false;
      }
    }
  } catch {
    // Ignore malformed cache
  }

  localStorage.setItem(LAST_SENT_KEY, JSON.stringify({ path: pathname, ts: now }));
  return true;
}

function sendVisit(pathname: string) {
  const visitorId = getVisitorId();
  if (!visitorId || !shouldSend(pathname)) return;

  const payload = JSON.stringify({
    visitorId,
    path: pathname,
    referrer: document.referrer || undefined,
  });

  const url = '/api/analytics/visit';
  const blob = new Blob([payload], { type: 'application/json' });

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    navigator.sendBeacon(url, blob);
    return;
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {
    // Silently ignore tracking errors.
  });
}

export default function SiteVisitTracker() {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname || pathname.startsWith('/admin') || pathname.startsWith('/api')) {
      return;
    }

    const timer = window.setTimeout(() => {
      sendVisit(pathname);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [pathname]);

  return null;
}
